import { PlanningConditions } from 'src/entities/PlanningHistory.entity';
import { RelaxedRuleVo } from 'src/vo/RelaxedRule.vo';

export type PriorityMode = 'none' | 'nearby' | 'far';
export type VehicleSizePreference = 'none' | 'small' | 'large';
export type DispatchModel = 'opus' | 'repro' | 'fusion';
// 配車シナリオ(#296)。LOG-3.0 Opus (DispatchModel 'fusion') 配下でのみ選べる。
// spillover_* は senario='spillover' + spillover_priority の組み合わせを 1 つの選択肢として表す
// (画面の「配車シナリオ」3 択 = アルゴリズムの senario 2 種 × priority の合成)。
export type DispatchScenario = 'normal' | 'spillover_duration' | 'spillover_trucks';

export type BuildPlanningConditionsSnapshotParams = {
  howToPlanning: PlanningConditions['howToPlanning'];
  priorityMode: PriorityMode;
  vehicleSizePreference: VehicleSizePreference;
  dispatchModel: DispatchModel;
  dispatchScenario: DispatchScenario;
  availabilityOfExpressway: boolean;
  includeFutureDeliveries: boolean;
  autoSplitOrders: boolean;
  dontExchangePreroute: boolean;
  concurrentAllOrNothing: boolean;
  activateIncrementalMaxNumPistons: boolean;
  selectedRelaxedRules: RelaxedRuleVo[];
  expandWorkingTimeStartMinutes: number;
  expandWorkingTimeEndMinutes: number;
  expandLoadStartMinutes: number;
  expandLoadEndMinutes: number;
  expandUnloadStartMinutes: number;
  expandUnloadEndMinutes: number;
  expandMaxVolumeRate: number;
  expandMaxLoadCapacityRate: number;
};

// How: 「エリア優先」トグルの内部値(none/nearby/far)と、履歴表示に使う日本語ラベル
//   (指定なし/近隣優先/遠方優先)を相互変換する。
// Why not algo_preset を導出しない: useQueryPlanning.ts は algo_preset を 'opt_center' に
//   固定しており、この選択値はどの画面でもアルゴリズムへ渡っていない（実装確認済み）。
//   本関数はあくまで履歴表示用の snapshot を作るだけで、配車アルゴリズムの挙動は変えない。
const PRIORITY_MODE_TO_PLANNING_PRIORITY: Record<PriorityMode, PlanningConditions['planningPriority']> = {
  none: '指定なし',
  nearby: '近隣優先',
  far: '遠方優先',
};

const PLANNING_PRIORITY_TO_PRIORITY_MODE: Record<PlanningConditions['planningPriority'], PriorityMode> = {
  指定なし: 'none',
  近隣優先: 'nearby',
  遠方優先: 'far',
};

export function priorityModeToPlanningPriority(priorityMode: PriorityMode): PlanningConditions['planningPriority'] {
  return PRIORITY_MODE_TO_PLANNING_PRIORITY[priorityMode];
}

export function planningPriorityToPriorityMode(planningPriority: PlanningConditions['planningPriority']): PriorityMode {
  return PLANNING_PRIORITY_TO_PRIORITY_MODE[planningPriority];
}

/**
 * PlanningPresenter (V3Plans/Map で共用) の画面設定 state から、localStorage 保存用と
 * シナリオ履歴 (onPlanningConditionsChange) 用の両方に渡す snapshot を組み立てる。
 * 呼び出し元ごとに object を手組みすると2箇所の定義が食い違うため、正本をこの純粋関数1つに絞る。
 *
 * Why not PlanningConditions の全フィールドを埋めない: isForcePlanning 等は V2Plans 専用の
 *   state で、V3Plans/Map の PlanningPresenter は元々これらを持たない（値も持ったことがない）。
 *   本関数の責務は「V3Plans/Map の PlanningPresenter が実際に持つ state」の snapshot 化であり、
 *   持たない値を埋め合わせることではない。
 */
export function buildPlanningConditionsSnapshot(params: BuildPlanningConditionsSnapshotParams): PlanningConditions {
  const {
    howToPlanning,
    priorityMode,
    vehicleSizePreference,
    dispatchModel,
    dispatchScenario,
    availabilityOfExpressway,
    includeFutureDeliveries,
    autoSplitOrders,
    dontExchangePreroute,
    concurrentAllOrNothing,
    activateIncrementalMaxNumPistons,
    selectedRelaxedRules,
    expandWorkingTimeStartMinutes,
    expandWorkingTimeEndMinutes,
    expandLoadStartMinutes,
    expandLoadEndMinutes,
    expandUnloadStartMinutes,
    expandUnloadEndMinutes,
    expandMaxVolumeRate,
    expandMaxLoadCapacityRate,
  } = params;

  return {
    howToPlanning,
    planningPriority: priorityModeToPlanningPriority(priorityMode),
    availabilityOfExpressway,
    includeFutureDeliveries,
    splitOrders: autoSplitOrders,
    dontExchangePreroute,
    concurrentAllOrNothing,
    activateIncrementalMaxNumPistons,
    selectedRelaxedRules,
    expandWorkingTimeStartMinutes,
    expandWorkingTimeEndMinutes,
    expandLoadStartMinutes,
    expandLoadEndMinutes,
    expandUnloadStartMinutes,
    expandUnloadEndMinutes,
    expandMaxVolumeRate,
    expandMaxLoadCapacityRate,
    vehicleSizePreference,
    dispatchModel,
    dispatchScenario,
  };
}
